// The tax line on a customer document — the ONE owner (operator 2026-10-20: "yes tax rate
// should be on pdf"). Prints e.g. "Tax (8.5% on $310.00)".
//
// Nothing here CALCULATES tax. The amount printed is always the SAVED tax_amount
// (recalculate_work_order_financials for a quote/job, create_invoice_from_work_order_v2_rpc
// for an invoice — PRODUCT_DECISIONS P8, 20261019_tax_single_source). These helpers only
// EXPLAIN that saved number: the rate it was charged at and the taxable base it was charged
// on. The base is rebuilt the way the DB built it, then checked against the saved amount;
// if the two do not reconcile to the cent the base is left off and only the rate prints, so
// the document can never show a base that contradicts its own tax figure.

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// recalculate_work_order_financials, TIME_MATERIALS branch: only these line types count. On every
// model a 'discount' row reduces the subtotal and a 'tax' row is never summed (20261022).
const TM_LINE_TYPES = ['labor', 'material', 'equipment', 'service', 'fee', 'permit', 'disposal', 'travel', 'discount'];
// Header-priced models: the DB taxes the whole (adjusted) header amount.
const WHOLE_AMOUNT_TAXED = ['FLAT_RATE', 'PERCENTAGE', 'UNIT', 'MILESTONE'];

const optionalExcluded = (it) => it?.is_optional === true && it?.optional_selected !== true;

/** 8.5 -> "8.5%", 8.25 -> "8.25%", 8.875 -> "8.875%". */
export const formatTaxRate = (rate) => `${parseFloat(num(rate).toFixed(4))}%`;

const reconciles = (base, rate, amount) =>
  base != null && rate > 0 && Math.abs(round2((base * rate) / 100) - round2(amount)) < 0.005;

/**
 * Tax summary for a quote / job (a work_orders row and its work_order_line_items).
 * kind: 'none' (no tax charged, not exempt) | 'exempt' | 'rate' | 'plain' (amount with no known rate)
 */
export function workOrderTaxSummary(wo, items) {
  const amount = num(wo?.tax_amount);
  const rate = num(wo?.tax_rate);
  if (amount === 0 && wo?.tax_exempt === true) return { kind: 'exempt', amount, rate: null, base: null, markNonTaxable: false };
  // No tax charged: no tax row, even when a rate is on file. A rate beside $0.00 reads as a
  // contradiction (same rule as the invoice drawer, job page and portal quote, 451ce8a1b).
  if (amount === 0) return { kind: 'none', amount, rate: null, base: null, markNonTaxable: false };
  if (rate <= 0) return { kind: 'plain', amount, rate: null, base: null, markNonTaxable: false };

  const model = String(wo?.pricing_model || '').toUpperCase();
  const fromLines = model === 'FLAT_RATE' && wo?.prices_from_line_items === true;
  let ratio = 1;
  let hasNonTaxable = false;
  if (!(WHOLE_AMOUNT_TAXED.includes(model) && !fromLines) && Array.isArray(items)) {
    const counted = items.filter((it) => {
      if (optionalExcluded(it)) return false;
      if (it?.line_type === 'tax') return false;
      if (model === 'TIME_MATERIALS') return TM_LINE_TYPES.includes(it?.line_type);
      if (fromLines) return it?.line_item_type !== 'system';
      return true;
    });
    const rawTotal = (it) => (it?.total_price != null ? num(it.total_price) : num(it?.quantity) * num(it?.unit_price));
    const lineTotal = (it) => (it?.line_type === 'discount' ? -Math.abs(rawTotal(it)) : rawTotal(it));
    const all = counted.reduce((s, it) => s + lineTotal(it), 0);
    const taxable = counted.filter((it) => it?.taxable !== false).reduce((s, it) => s + lineTotal(it), 0);
    hasNonTaxable = counted.some((it) => it?.taxable === false);
    ratio = all > 0 ? taxable / all : 1;
  }
  const adjusted = Math.max(num(wo?.subtotal) + num(wo?.adjustment_total), 0);
  const base = round2(adjusted * ratio);
  const ok = reconciles(base, rate, amount);
  return { kind: 'rate', amount, rate, base: ok ? base : null, markNonTaxable: ok && hasNonTaxable };
}

/**
 * Tax summary for an invoice. Each invoice_line_items row carries the rate it was taxed at
 * (the work order rate, 0 on a non-taxable line, a prorated rate on an adjustment line), and
 * invoices.tax_amount = sum(quantity x unit_price x tax_rate / 100). The invoice's rate is the
 * highest line rate; the work order's rate is only a fallback when no line carries one.
 */
export function invoiceTaxSummary(invoice, items, workOrder = null) {
  const amount = num(invoice?.tax_amount);
  const lines = (Array.isArray(items) ? items : []).filter((it) => it && it.tax_rate != null);
  const maxLineRate = lines.reduce((m, it) => Math.max(m, num(it.tax_rate)), 0);
  const rate = maxLineRate > 0 ? maxLineRate : num(workOrder?.tax_rate);
  if (amount === 0 && workOrder?.tax_exempt === true) return { kind: 'exempt', amount, rate: null, base: null, markNonTaxable: false };
  if (amount === 0) return { kind: 'none', amount, rate: null, base: null, markNonTaxable: false };
  if (rate <= 0) return { kind: 'plain', amount, rate: null, base: null, markNonTaxable: false };

  // No line carries a rate (e.g. the PDF fell back to the job's lines, whose tax_rate is 0):
  // the lines cannot say what was taxed, so no base and no "(non-taxable)" notes.
  let base = null;
  if (maxLineRate > 0) {
    const taxed = lines.reduce(
      (s, it) => s + (it.quantity == null ? 1 : num(it.quantity)) * num(it.unit_price) * num(it.tax_rate), 0
    );
    base = round2(taxed / maxLineRate);
  }
  const ok = reconciles(base, rate, amount);
  return {
    kind: 'rate',
    amount,
    rate,
    base: ok ? base : null,
    markNonTaxable: ok && lines.some((it) => num(it.tax_rate) === 0),
  };
}

/** The label for the tax row, or null when the document should print no tax row. */
export function taxLineLabel(summary, fmtMoney) {
  if (!summary) return 'Tax';
  switch (summary.kind) {
    case 'none': return null;
    case 'exempt': return 'Tax exempt';
    case 'rate':
      return summary.base != null
        ? `Tax (${formatTaxRate(summary.rate)} on ${fmtMoney(summary.base)})`
        : `Tax (${formatTaxRate(summary.rate)})`;
    default: return 'Tax';
  }
}

/** Should this line carry a "(non-taxable)" note? Only when the document shows a base. */
export const isNonTaxableWorkOrderLine = (summary, item) => !!summary?.markNonTaxable && item?.taxable === false;
export const isNonTaxableInvoiceLine = (summary, item) =>
  !!summary?.markNonTaxable && item?.tax_rate != null && num(item.tax_rate) === 0;
